import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash, Eye, EyeSlash, Copy, Lock } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useJobPortals } from "@/hooks/useMasterData";

const KIND_LABELS = {
  portal: "Portal login",
  marketing_email: "Marketing email",
  linkedin: "LinkedIn",
  other: "Other",
};

const EMPTY = {
  portal_id: "",
  portal_name: "",
  kind: "portal",
  username: "",
  password: "",
  recovery_email: "",
  notes: "",
};

export default function CandidateCredentials({ candidateId, allowed }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState({}); // id → plaintext
  const { items: portals } = useJobPortals();

  const load = useCallback(async () => {
    if (!allowed) return;
    try {
      const { data } = await api.get(`/candidates/${candidateId}/credentials`);
      setRows(data);
    } catch (ex) {
      if (ex.response?.status !== 403) toast.error(formatApiError(ex.response?.data?.detail));
    }
  }, [candidateId, allowed]);

  useEffect(() => { load(); }, [load]);

  if (!allowed) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="cand-credentials-locked">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Lock size={16} /> Candidate credentials are restricted to Admin and Team Lead.
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      // If portal_id is chosen, portal_name is auto-set on server. If not, we need portal_name.
      if (!payload.portal_id && !payload.portal_name) {
        toast.error("Choose a portal or enter a custom label");
        setBusy(false);
        return;
      }
      await api.post(`/candidates/${candidateId}/credentials`, payload);
      toast.success("Credential saved");
      setForm(EMPTY);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reveal = async (row) => {
    if (revealed[row.id]) {
      setRevealed((r) => { const n = { ...r }; delete n[row.id]; return n; });
      return;
    }
    try {
      const { data } = await api.post(`/candidates/${candidateId}/credentials/${row.id}/reveal`);
      setRevealed((r) => ({ ...r, [row.id]: data.password }));
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const copy = async (row) => {
    let text = revealed[row.id];
    if (!text) {
      const { data } = await api.post(`/candidates/${candidateId}/credentials/${row.id}/reveal`);
      text = data.password;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Password copied");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete credential for ${row.portal_name}?`)) return;
    try {
      await api.delete(`/candidates/${candidateId}/credentials/${row.id}`);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="cand-credentials">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Lock size={16} /> Candidate Credentials
        </h3>
        <span className="text-xs text-slate-500">Encrypted at rest · Admin + Team Lead only</span>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-2 bg-slate-50 border border-slate-200 rounded-md p-3">
        <select
          value={form.kind}
          onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
          data-testid="cred-kind"
          className="border border-slate-200 rounded-md px-2 py-2 text-sm md:col-span-1"
        >
          {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select
          value={form.portal_id}
          onChange={(e) => {
            const id = e.target.value;
            const p = portals.find((x) => x.id === id);
            setForm((f) => ({ ...f, portal_id: id, portal_name: p ? p.name : "" }));
          }}
          data-testid="cred-portal"
          className="border border-slate-200 rounded-md px-2 py-2 text-sm md:col-span-1"
        >
          <option value="">— pick portal —</option>
          {portals.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input
          placeholder="Custom label (if no portal)"
          value={form.portal_id ? "" : form.portal_name}
          onChange={(e) => setForm((f) => ({ ...f, portal_name: e.target.value, portal_id: "" }))}
          data-testid="cred-portal-name"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm md:col-span-1"
        />
        <input
          required
          placeholder="Username / email"
          value={form.username}
          onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
          data-testid="cred-username"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm md:col-span-1"
        />
        <input
          type="password"
          placeholder="Password"
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          data-testid="cred-password"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm md:col-span-1"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="cred-submit"
          className="bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-sm rounded-md px-3 py-2 inline-flex items-center justify-center gap-1"
        >
          <Plus size={14} /> Save
        </button>
      </form>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Portal / Type</th>
              <th className="text-left px-3 py-2">Username</th>
              <th className="text-left px-3 py-2">Password</th>
              <th className="text-left px-3 py-2">Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="text-center text-slate-400 py-6">No credentials on file.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-800">{r.portal_name}</div>
                  <div className="text-xs text-slate-500">{KIND_LABELS[r.kind] || r.kind}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{r.username}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  <div className="flex items-center gap-2">
                    <span data-testid={`cred-pwd-${r.id}`}>
                      {revealed[r.id] ? revealed[r.id] : r.password_masked || "—"}
                    </span>
                    {r.has_password && (
                      <>
                        <button onClick={() => reveal(r)} className="text-slate-400 hover:text-slate-700" data-testid={`cred-reveal-${r.id}`}>
                          {revealed[r.id] ? <EyeSlash size={14} /> : <Eye size={14} />}
                        </button>
                        <button onClick={() => copy(r)} className="text-slate-400 hover:text-slate-700" data-testid={`cred-copy-${r.id}`}>
                          <Copy size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {new Date(r.updated_at).toLocaleDateString()}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => remove(r)} className="text-red-600 hover:text-red-800" data-testid={`cred-delete-${r.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
